import { useEffect, useMemo, type ReactNode } from "react";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { I18nextProvider, useTranslation } from "react-i18next";
import type { i18n } from "i18next";
import {
  changeAppLanguage,
  createAppQueryClient,
  ErrorBoundary,
  TenantProvider,
  TenantThemeApplier,
  TenantTitleApplier,
  directionFor,
  subscribeToToasts,
  useLanguageStore,
  useTenant,
  type ErrorFallbackProps,
} from "@nasam/core";
import { Button, DirectionProvider, Toaster } from "@nasam/ui";
import { SessionTimeoutListener } from "../shell/SessionTimeoutDialog";
import { tenantRepository } from "../tenant";

/**
 * The Origin this app is running on doesn't name a real tenant (backend
 * confirmed via `GET /tenants/resolve`) — shown in place of the whole app,
 * router included, rather than letting a stray address fall through to a
 * generic login screen or the browser's own dead end.
 *
 * Same full-bleed illustrated shell as LinkStatusScreen's "expired/invalid
 * link" template (rounded panel, centered illustration, large title) —
 * duplicated here rather than imported, since that component lives under
 * the auth feature and this renders above the router entirely, outside any
 * feature boundary.
 */
function TenantNotFoundScreen() {
  const { t, i18n } = useTranslation();
  return (
    <main
      role="alert"
      dir={i18n.dir()}
      className="animate-rise flex h-screen items-center justify-center overflow-hidden bg-neutral-100 p-4 lg:p-5"
    >
      <div className="flex h-full w-full max-w-[87.4375rem] flex-col items-center justify-center gap-4 overflow-hidden rounded-lg bg-container-default-background px-6 py-6 text-center lg:gap-9 lg:py-16">
        <img
          src="/brand/link-status-illustration.png"
          alt=""
          aria-hidden="true"
          className="max-h-[40vh] w-auto max-w-[30rem] object-contain"
        />
        <div className="flex flex-col items-center gap-2">
          <h1 className="text-heading-h2-bold text-text">
            {t("common:tenantNotFound.title")}
          </h1>
          <p className="text-heading-h5-semibold text-text-placeholder">
            {t("common:tenantNotFound.description")}
          </p>
        </div>
      </div>
    </main>
  );
}

/** Must render inside TenantProvider, so it can read the resolve outcome. */
function TenantGate({ children }: { readonly children: ReactNode }) {
  const { tenantNotFound } = useTenant();
  if (tenantNotFound) return <TenantNotFoundScreen />;
  return <>{children}</>;
}

export interface AppProvidersProps {
  readonly children: ReactNode;
  readonly i18n: i18n;
  readonly queryClient?: QueryClient;
  readonly tenantHostname?: string;
  readonly tenantDevOverride?: string;
}

export function AppProviders({
  children,
  i18n,
  queryClient,
  tenantHostname,
  tenantDevOverride,
}: AppProvidersProps) {
  const language = useLanguageStore((state) => state.language);
  const resolvedQueryClient = useMemo(
    () => queryClient ?? createAppQueryClient(),
    [queryClient],
  );

  useEffect(() => {
    return useLanguageStore.subscribe((state) => {
      if (i18n.language !== state.language) {
        void changeAppLanguage(i18n, state.language);
      }
    });
  }, [i18n]);

  const renderFallback = ({ error, reset }: ErrorFallbackProps): ReactNode => {
    const title =
      typeof i18n.t === "function"
        ? i18n.t("common:somethingWentWrong")
        : "Something went wrong. Please try again.";
    const retryText =
      typeof i18n.t === "function" ? i18n.t("common:confirm") : "Retry";

    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center p-6 text-center"
      >
        <div className="max-w-md space-y-4">
          <h1 className="text-heading-h3-bold text-text">{title}</h1>
          <p className="text-body-m-r text-text-placeholder">{error.message}</p>
          <Button variant="primary" onClick={reset}>
            {retryText}
          </Button>
        </div>
      </div>
    );
  };

  const devOverrideValue =
    tenantDevOverride ??
    (import.meta.env.VITE_DEV_TENANT
      ? import.meta.env.VITE_DEV_TENANT
      : undefined);

  return (
    <DirectionProvider dir={directionFor(language)}>
      <ErrorBoundary fallback={renderFallback}>
        <QueryClientProvider client={resolvedQueryClient}>
          <I18nextProvider i18n={i18n}>
            <TenantProvider
              // TEMPORARY: /tenants/branding is currently broken on the
              // backend, and its query only fires when loadBranding is
              // defined -- so leaving it unset skips that call entirely.
              // Restore once /tenants/branding is fixed.
              // loadBranding={() => tenantRepository.getBranding()}
              resolveTenant={() => tenantRepository.resolve()}
              {...(tenantHostname !== undefined
                ? { hostname: tenantHostname }
                : {})}
              {...(devOverrideValue !== undefined
                ? { devOverride: devOverrideValue }
                : {})}
            >
              <TenantThemeApplier />
              <TenantTitleApplier />
              <SessionTimeoutListener />
              <TenantGate>{children}</TenantGate>
              <Toaster subscribe={subscribeToToasts} />
            </TenantProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </DirectionProvider>
  );
}
